import moment from 'moment';
import React from 'react';
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { CSVLink } from 'react-csv';
import DataGrid, {
  Row as DataGridRow,
  SelectColumn,
  SortableHeaderCell,
} from 'react-data-grid';
import { withTranslation } from 'react-i18next';

import { getMyAccountData } from '../actions/account';
import { getMyBadges } from '../actions/badges';
import { getContact } from '../actions/contacts';
import DeleteIcon from '../assets/icons/delete.png';
import AddNewButton from '../components/AddNewButton';
import EditBadgeModal from '../components/EditBadgeModal';
import EditContactModal from '../components/EditContactModal';
import COLORS from '../constants/colors';
import globalStyles from '../stylesheets/globalStyles';

const i18nPrefix = 'BadgeTable';

const tableLinkStyle = {
  color: COLORS.MAIN_BLUE,
  textDecoration: 'underline',
  cursor: 'pointer',
};

const parseContactIdFromLocation = (location) => {
  if (location?.state?.contact_id) {
    return String(location.state.contact_id);
  }

  const search = location?.search || '';
  const params = new URLSearchParams(search);
  const fromQuery = params.get('contact_id');
  return fromQuery || '';
};

function rowKeyGetter(row) {
  return row.id;
}

class BadgeTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      badges: [],
      filteredBadges: [],
      selectedRows: new Set([]),
      selectedBadges: [],
      export_scope: 'selected',
      filters: {
        global: '',
        include_inactive: false,
        contact_id: parseContactIdFromLocation(props.location),
      },
      columnSortings: [],
      account: {},
    };

    this.csvLink = React.createRef();
  }

  componentDidMount() {
    getMyAccountData(
      (account) => this.setState({ account }),
      () => {},
    );

    this.loadBadges();
  }

  loadBadges = () => {
    this.setState({ loading: true }, () => {
      getMyBadges(
        (badges) => {
          const normalizedBadges = this.normalizeRows(badges);
          const filteredBadges = this.getFilteredRows(
            normalizedBadges,
            this.state.filters,
          );
          const sortedFiltered = this.getSortedRows(
            filteredBadges,
            this.state.columnSortings,
          );

          this.setState({
            badges: normalizedBadges,
            filteredBadges: sortedFiltered,
            selectedRows: new Set([]),
            selectedBadges: [],
            loading: false,
          });
        },
        () => {
          this.setState({ loading: false });
        },
        {
          include_inactive: 1,
        },
      );
    });
  };

  normalizeRows = (badges) => {
    return (badges || []).map((badge) => {
      const contact = badge.contact || {};
      const contactAddress = [contact.street, contact.zip, contact.city]
        .filter(Boolean)
        .join(' ');

      return {
        ...badge,
        contact,
        comment: badge.comment || '',
        contact_id: badge.contact_id || contact.id || '',
        contact_salutation: contact.salutation || '',
        contact_firstname: contact.firstname || '',
        contact_lastname: contact.lastname || '',
        contact_address: contactAddress,
        contact_email: contact.email || '',
        is_active: !badge.redemption_date,
      };
    });
  };

  setFilters = (filters) => {
    const filteredBadges = this.getFilteredRows(this.state.badges, filters);
    const sortedFiltered = this.getSortedRows(
      filteredBadges,
      this.state.columnSortings,
    );

    this.setState({
      filters,
      filteredBadges: sortedFiltered,
    });
  };

  getFilteredRows = (badges, filters = this.state.filters) => {
    let filtered = [...badges];

    if (!filters.include_inactive) {
      filtered = filtered.filter(
        (badge) => badge.is_active || !badge.deposit_refunded,
      );
    }

    if (filters.contact_id) {
      filtered = filtered.filter(
        (badge) => String(badge.contact_id) === String(filters.contact_id),
      );
    }

    if (filters.global) {
      const searchText = filters.global.replace(/ /g, '').toLowerCase();
      filtered = filtered.filter((badge) => {
        const name = `${badge.contact_firstname || ''}${badge.contact_lastname || ''}`
          .replace(/ /g, '')
          .toLowerCase();
        const badgeNumber = String(badge.badge_number || '')
          .replace(/ /g, '')
          .toLowerCase();
        const contactId = String(badge.contact_id || '')
          .replace(/ /g, '')
          .toLowerCase();

        return (
          name.includes(searchText) ||
          badgeNumber.includes(searchText) ||
          contactId.includes(searchText)
        );
      });
    }

    return filtered;
  };

  getSortedRows = (badges, columnSortings = this.state.columnSortings) => {
    const sorted = [...badges].sort((a, b) => {
      if (a.is_active !== b.is_active) {
        return a.is_active ? -1 : 1;
      }

      const columnKey = columnSortings?.[0]?.columnKey;
      const direction = columnSortings?.[0]?.direction || 'DESC';

      const getComparisonByColumn = () => {
        if (!columnKey || columnKey === 'is_active') {
          const dateA = moment(a.issue_date);
          const dateB = moment(b.issue_date);
          return dateA.isAfter(dateB) ? 1 : dateA.isBefore(dateB) ? -1 : 0;
        }

        const valueA = a[columnKey] ?? '';
        const valueB = b[columnKey] ?? '';

        if (columnKey === 'issue_date' || columnKey === 'redemption_date') {
          const dateA = moment(valueA);
          const dateB = moment(valueB);
          if (dateA.isValid() && dateB.isValid()) {
            return dateA.isAfter(dateB) ? 1 : dateA.isBefore(dateB) ? -1 : 0;
          }
        }

        if (columnKey === 'deposit_amount') {
          return parseFloat(valueA || 0) - parseFloat(valueB || 0);
        }

        return String(valueA).localeCompare(String(valueB));
      };

      const comparison = getComparisonByColumn();
      return direction === 'ASC' ? comparison : -comparison;
    });

    return sorted;
  };

  onSortColumnsChange = (columnSortings) => {
    const sorted = this.getSortedRows(
      this.state.filteredBadges,
      columnSortings,
    );
    this.setState({ columnSortings, filteredBadges: sorted });
  };

  exportToCSVClicked = () => {
    this.csvLink.current.link.click();
  };

  getExportRows = () => {
    if (this.state.export_scope === 'selected') {
      return this.state.selectedBadges.map((badge) => ({
        id: badge.id,
        badge_number: badge.badge_number,
        comment: badge.comment,
        issue_date: badge.issue_date,
        deposit_amount: badge.deposit_amount,
        redemption_date: badge.redemption_date,
        deposit_refunded: badge.deposit_refunded,
        contact_id: badge.contact_id,
        salutation: badge.contact_salutation,
        firstname: badge.contact_firstname,
        lastname: badge.contact_lastname,
        address: badge.contact_address,
        email: badge.contact_email,
      }));
    }

    const rows = this.getFilteredRows(this.state.badges, this.state.filters);
    const sortedRows = this.getSortedRows(rows, this.state.columnSortings);

    return sortedRows.map((badge) => ({
      id: badge.id,
      badge_number: badge.badge_number,
      comment: badge.comment,
      issue_date: badge.issue_date,
      deposit_amount: badge.deposit_amount,
      redemption_date: badge.redemption_date,
      deposit_refunded: badge.deposit_refunded,
      contact_id: badge.contact_id,
      salutation: badge.contact_salutation,
      firstname: badge.contact_firstname,
      lastname: badge.contact_lastname,
      address: badge.contact_address,
      email: badge.contact_email,
    }));
  };

  RowRenderer = ({ ...props }) => {
    const className = props.row.is_active ? '' : 'row-highlight-illegal';

    return (
      <div className={className}>
        <DataGridRow {...props} />
      </div>
    );
  };

  onSelectedRowsChange = (selectedRows) => {
    const selected_badge_ids = Array.from(selectedRows);
    const selectedBadges = selected_badge_ids
      .map((id) => this.state.badges.find((badge) => badge.id === id))
      .filter(Boolean);

    this.setState({ selectedRows, selectedBadges });
  };

  render() {
    const { t } = this.props;
    const { filters } = this.state;
    const isBadgeManagementEnabled = (val) =>
      [1, '1', true].includes(val);
    const storedAccount = (() => {
      try {
        return JSON.parse(localStorage.getItem('AccountData') || '{}');
      } catch {
        return {};
      }
    })();
    const showBadgeManagement =
      isBadgeManagementEnabled(this.state.account?.show_badge_management) ||
      isBadgeManagementEnabled(storedAccount?.show_badge_management);

    if (
      this.state.account &&
      Object.keys(this.state.account).length &&
      !showBadgeManagement
    ) {
      return null;
    }

    const columns = [
      SelectColumn,
      {
        key: 'badge_number',
        name: t(`${i18nPrefix}.badgeNumber`, 'Badge number'),
        width: 160,
        frozen: true,
        formatter: (props) => (
          <a
            href='#'
            style={tableLinkStyle}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              this.EditBadgeModal.show(props.row);
            }}>
            {props.row.badge_number}
          </a>
        ),
      },
      {
        key: 'issue_date',
        name: t(`${i18nPrefix}.issueDate`, 'Issue date'),
        width: 140,
      },
      {
        key: 'comment',
        name: t(`${i18nPrefix}.comment`, 'Comment'),
        width: 220,
        formatter: (props) => (
          <div
            title={props.row.comment || ''}
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
            {props.row.comment || ''}
          </div>
        ),
      },
      {
        key: 'deposit_amount',
        name: t(`${i18nPrefix}.depositAmount`, 'Deposit amount'),
        width: 140,
      },
      {
        key: 'redemption_date',
        name: t(`${i18nPrefix}.redemptionDate`, 'Redemption date'),
        width: 150,
      },
      {
        key: 'deposit_refunded',
        name: t(`${i18nPrefix}.depositRefunded`, 'Deposit refunded'),
        width: 160,
        formatter: (props) =>
          props.row.deposit_refunded
            ? t('global.YesOrNo.1', 'Yes')
            : t('global.YesOrNo.0', 'No'),
      },
      {
        key: 'contact_id',
        name: t(`${i18nPrefix}.contactId`, 'Contact ID'),
        width: 120,
        formatter: (props) => {
          const contactId = props.row.contact_id;
          if (!contactId) return null;

          return (
            <a
              href='#'
              style={tableLinkStyle}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                getContact(
                  contactId,
                  (contact) => this.EditContactModal.show(contact),
                  () => {},
                );
              }}>
              {contactId}
            </a>
          );
        },
      },
      {
        key: 'contact_salutation',
        name: t(`${i18nPrefix}.salutation`, 'Salutation'),
        width: 140,
      },
      {
        key: 'contact_firstname',
        name: t(`${i18nPrefix}.firstName`, 'First name'),
        width: 140,
      },
      {
        key: 'contact_lastname',
        name: t(`${i18nPrefix}.lastName`, 'Last name'),
        width: 140,
      },
      {
        key: 'contact_address',
        name: t(`${i18nPrefix}.address`, 'Address'),
        width: 220,
      },
      {
        key: 'contact_email',
        name: t(`${i18nPrefix}.email`, 'Email'),
        width: 220,
      },
      {
        key: 'edit',
        name: '',
        width: 110,
        formatter: (props) => (
          <Button
            style={{ ...globalStyles.normalButton, height: 30, marginTop: 2 }}
            onClick={(event) => {
              event.stopPropagation();
              this.EditBadgeModal.show(props.row);
            }}>
            {t('global.edit', 'Edit')}
          </Button>
        ),
      },
    ];

    return (
      <>
        <EditBadgeModal
          onRef={(ref) => {
            this.EditBadgeModal = ref;
          }}
          onSave={this.loadBadges}
        />
        <EditContactModal
          onRef={(ref) => {
            this.EditContactModal = ref;
          }}
          onSave={this.loadBadges}
        />

        <Row>
          <Col sm={5}>
            {this.state.loading && (
              <div style={{ marginTop: 12 }}>
                <Spinner
                  style={{ float: 'left', marginLeft: 10 }}
                  animation='border'
                />
              </div>
            )}
          </Col>
          <Col sm={7}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 10,
              }}>
              <Button
                style={{ ...globalStyles.saveButton, marginLeft: 0 }}
                disabled={this.state.loading}
                onClick={this.exportToCSVClicked}>
                {t('global.export')}
              </Button>
              {/* <Form.Control
                as='select'
                style={{ width: 150, marginBottom: 0 }}
                value={this.state.export_scope}
                onChange={(e) =>
                  this.setState({ export_scope: e.target.value })
                }>
                <option value='selected'>
                  {t(`${i18nPrefix}.exportSelected`, 'Only selected')}
                </option>
                <option value='all'>{t(`${i18nPrefix}.exportAll`, 'All')}</option>
              </Form.Control> */}
            </div>
          </Col>
        </Row>

        <CSVLink
          separator={this.state.account.csv_separator}
          style={{ visibility: 'hidden' }}
          ref={this.csvLink}
          filename='BP-badges.csv'
          data={this.getExportRows()}>
          Download me
        </CSVLink>

        <Row>
          <Col md={5}>
            <input
              style={{
                marginTop: 0,
                outline: 'none',
                marginBottom: 10,
                width: '100%',
                border: 'none',
                borderRadius: 0,
                paddingLeft: 0,
                backgroundColor: 'white',
                color: COLORS.MAIN_BLUE,
                boxShadow: 'none',
              }}
              placeholder={t('global.search')}
              value={filters.global}
              onChange={(e) =>
                this.setFilters({
                  ...filters,
                  global: e.target.value,
                })
              }
            />
            <div
              style={{
                width: '100%',
                height: 1,
                backgroundColor: COLORS.GRAY_OUTLINE,
              }}></div>

            <div
              style={{ display: 'flex', alignItems: 'center', marginTop: 8 }}>
              <Form.Control
                value={filters.contact_id}
                placeholder={t(`${i18nPrefix}.contactId`, 'Contact ID')}
                onChange={(e) =>
                  this.setFilters({
                    ...filters,
                    contact_id: e.target.value,
                  })
                }
                style={{ marginTop: 0, marginBottom: 0, maxWidth: 180 }}
              />
              <button
                onClick={() =>
                  this.setFilters({
                    ...filters,
                    contact_id: '',
                  })
                }
                style={{
                  backgroundColor: 'transparent',
                  borderColor: 'transparent',
                  marginLeft: 4,
                }}>
                <img
                  style={{ width: 20, marginTop: 2 }}
                  src={DeleteIcon}
                  alt='clearFilterIcon'
                />
              </button>
            </div>
          </Col>

          <Col md={3}>
            <Form.Check
              id='include_inactive_badges'
              type='checkbox'
              label={t(`${i18nPrefix}.includeInactive`, 'Include inactive')}
              checked={filters.include_inactive}
              onChange={(e) => {
                this.setFilters({
                  ...filters,
                  include_inactive: e.target.checked,
                });
              }}
              style={{ marginTop: 8 }}
            />
          </Col>
          <Col md={4} style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <AddNewButton
              style={{ marginBottom: 0, maxWidth: 260 }}
              label={t(`${i18nPrefix}.addBadge`, 'Add badge')}
              onClick={() => this.EditBadgeModal.show()}
            />
          </Col>
        </Row>

        <div style={{ marginTop: 15 }}>
          <DataGrid
            style={{ maxHeight: 'none', height: 700 }}
            columns={columns}
            rows={this.state.filteredBadges}
            className='rdg-light'
            rowKeyGetter={rowKeyGetter}
            defaultColumnOptions={{
              sortable: true,
              resizable: true,
              editable: false,
            }}
            rowRenderer={this.RowRenderer}
            onSortColumnsChange={this.onSortColumnsChange}
            sortColumns={this.state.columnSortings}
            selectedRows={this.state.selectedRows}
            onSelectedRowsChange={this.onSelectedRowsChange}>
            <SortableHeaderCell />
          </DataGrid>
        </div>
      </>
    );
  }
}

const BadgeTableWithTranslation = withTranslation()(BadgeTable);
BadgeTableWithTranslation.displayName = 'BadgeTable';
export { BadgeTable };
export default BadgeTableWithTranslation;
